// relative-time.ts — "8 hours ago", "5 min ago", "Just now".
//
// The home screen's time columns used to render `fmtWhen()`: a raw UTC stamp,
// `2026-08-24 14:32`. The owner's requirement is relative time that updates
// live, and that a currently-active VM reads "Just now".
//
// WHY NOT pi-stack's formatRelative(). The console already depends on pi-stack
// and it exports one — but it renders the COMPACT form ("8h ago", "5m ago",
// "now"), which is right for a dense terminal chrome and wrong for a table
// whose whole job is legibility at a glance. The requirement quotes the long
// form verbatim. Rather than reformat pi-stack's output (parsing a string back
// into units is how these drift), this is its own derivation from the same
// input, and the ONE place the home screen's wording lives.
//
// UTC-SAFE. `new Date(iso)` treats a bare `2026-08-24 14:32:00` — no zone
// suffix — as LOCAL time, so a timestamp Postgres rendered without an offset
// would read hours off, and always in the direction that makes recent things
// look stale. Everything the console serves comes from `timestamptz` and
// carries a zone, but the normalizer below makes that not matter.

/** Below this, a timestamp reads "Just now" rather than a count.
 *
 * NOT an arbitrary number: `releases.last_vm_active_at` is flushed by the
 * proxy every 60 s, so the freshest truthful reading of a LIVE VM is already
 * up to a minute old. 90 s is that window plus half of one — the tightest
 * threshold that will not flicker a genuinely busy VM back to "1 min ago"
 * between two flushes. Mirrors VM_ACTIVE_WINDOW_MS in src/server/recency.ts. */
export const JUST_NOW_MS = 90_000;

/** How often the live columns re-render. One second would be wasted work for a
 * scale whose smallest step is a minute; 30 s means "5 min ago" is never more
 * than half a step stale. */
export const TICK_MS = 30_000;

// THE THREE PURE TIME PRIMITIVES MOVED OUT (#4309), and are re-exported here
// so this module's public surface is unchanged. They left because the VM
// standing derivation needs them and cannot import this file: the two tickers
// at the bottom call `window.setInterval`, so a server render path can never
// load it. The primitives themselves were always pure — only their ADDRESS
// was browser-only. One copy lives in ../server/vm-standing.ts; nothing here
// re-implements them.
import { parseStamp, absoluteStamp, formatUptime, UPTIME_TICK_MS } from '../server/vm-standing.js';
export { parseStamp, absoluteStamp, formatUptime, UPTIME_TICK_MS } from '../server/vm-standing.js';

/** Singular/plural without a lookup table, e.g. `unit(1,'hour')` -> "1 hour". */
function unit(n: number, name: string): string {
  return `${n} ${name}${n === 1 ? '' : 's'}`;
}

/**
 * The long relative form.
 *
 * `now` is injectable so the tests pin exact boundaries instead of sleeping —
 * a time formatter tested against the real clock can only be checked loosely,
 * and the boundaries are the whole behaviour.
 *
 * A FUTURE timestamp reads "Just now" rather than "in 3 minutes": small
 * negatives are clock skew between the box and the browser, not a real event,
 * and inventing a future tense for a column titled "last activity" would be
 * reporting something that has not happened.
 */
export function formatRelativeLong(
  iso: string | null | undefined,
  now: number = Date.now(),
): string {
  const d = parseStamp(iso);
  if (!d) return '—';
  const diff = now - d.getTime();
  if (diff < JUST_NOW_MS) return 'Just now';

  const min = Math.floor(diff / 60_000);
  // "min" is an ABBREVIATION and does not take a plural — the requirement's own
  // example is "5 min ago". The spelled-out units below do pluralize, which is
  // why this one is not routed through unit().
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${unit(hr, 'hour')} ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${unit(day, 'day')} ago`;
  const mon = Math.floor(day / 30);
  if (mon < 12) return `${unit(mon, 'month')} ago`;
  return `${unit(Math.floor(mon / 12), 'year')} ago`;
}


/**
 * VM activity, which has one extra state the other columns do not: a machine
 * that is running RIGHT NOW.
 *
 * "Just now" here is a claim about a live VM, and it is only as true as the
 * flush cadence allows — which is exactly why the threshold is derived from
 * that cadence rather than picked. A release that has never run anything reads
 * "—", never "Just now" and never the epoch.
 */
export function formatVmActivity(
  iso: string | null | undefined,
  now: number = Date.now(),
): string {
  if (!parseStamp(iso)) return '—';
  return formatRelativeLong(iso, now);
}



/**
 * Drive every `[data-uptime]` element under `root` from its own `data-uptime`
 * attribute (the VM's start stamp), repainting each second — the counting-up
 * twin of startRelativeTimeTicker, same contract: the cell paints its own
 * initial text at render so it is never blank, the ticker keeps it moving,
 * and the returned teardown MUST run on unmount (the home screen re-mounts
 * per navigation; a leaked interval walks a dead DOM forever). Re-querying
 * per tick — not caching elements — is what makes a grid rebuild or a cell
 * remount transparent: whatever is on the page NOW is what gets painted.
 */
export function startUptimeTicker(
  root: ParentNode,
  intervalMs: number = UPTIME_TICK_MS,
): () => void {
  const paint = (): void => {
    for (const el of root.querySelectorAll<HTMLElement>('[data-uptime]')) {
      el.textContent = formatUptime(el.dataset.uptime ?? '');
    }
  };
  paint();
  const timer = window.setInterval(paint, intervalMs);
  return () => window.clearInterval(timer);
}

/**
 * Drive every `[data-rel-time]` element under `root` from its own
 * `data-rel-time` attribute, and keep driving it.
 *
 * Returns a teardown. Callers MUST call it on unmount: the home screen
 * re-mounts on every hash route change, and an interval left behind would
 * accumulate one timer per navigation, each walking a DOM that is no longer on
 * the page.
 */
export function startRelativeTimeTicker(
  root: ParentNode,
  intervalMs: number = TICK_MS,
): () => void {
  const paint = (): void => {
    for (const el of root.querySelectorAll<HTMLElement>('[data-rel-time]')) {
      const iso = el.dataset.relTime ?? '';
      const vm = el.dataset.relKind === 'vm';
      el.textContent = vm ? formatVmActivity(iso) : formatRelativeLong(iso);
      const abs = absoluteStamp(iso);
      if (abs) el.dataset.tip = abs;
    }
  };
  paint();
  const timer = window.setInterval(paint, intervalMs);
  return () => window.clearInterval(timer);
}
